import { Api, ApiError } from '@/infrastructure';
import { BankAccount } from '@/types';
import { useCallback } from 'react';

type BankAccountRequestBody = {
  [key: string]: Record<string, unknown>;
};

// 422 は入力値の不備・口座の二重登録で、画面に出せば済む想定内のエラー。
// 想定外のエラーはそのまま throw して Sentry に届ける。
export type BankAccountResult = { ok: true } | { ok: false; errors: string[] };

type UseBankAccountHook = {
  fetchBankAccount: () => Promise<BankAccount>;
  createBankAccount: (
    requestBody: BankAccountRequestBody
  ) => Promise<BankAccountResult>;
  updateBankAccount: (
    requestBody: BankAccountRequestBody
  ) => Promise<BankAccountResult>;
};

// 422 のとき API は { error: [...] } を返す(ApiPartner::BankAccountsController)。
// 型のない外部入力なので実行時に配列であることを確かめる。
const validationErrors = (error: unknown, fallback: string): string[] | null => {
  if (!(error instanceof ApiError) || error.status !== 422) return null;

  const body = error.body as { error?: unknown } | undefined;
  if (!Array.isArray(body?.error) || body.error.length === 0) return [fallback];

  return body.error.map(String);
};

export const useBankAccount = (): UseBankAccountHook => {
  const fetchBankAccount = useCallback(async (): Promise<BankAccount> => {
    const response = await Api.get<BankAccount>('bank_accounts', 'partner');
    return response.data;
  }, []);

  const createBankAccount = async (
    requestBody: BankAccountRequestBody
  ): Promise<BankAccountResult> => {
    try {
      await Api.post('bank_accounts', 'partner', requestBody);
      return { ok: true };
    } catch (error) {
      const errors = validationErrors(error, '銀行口座の作成に失敗しました');
      if (!errors) throw error;

      return { ok: false, errors };
    }
  };

  const updateBankAccount = async (
    requestBody: BankAccountRequestBody
  ): Promise<BankAccountResult> => {
    try {
      await Api.patch('bank_accounts', 'partner', requestBody);
      return { ok: true };
    } catch (error) {
      const errors = validationErrors(error, '銀行口座情報の更新に失敗しました');
      if (!errors) throw error;

      return { ok: false, errors };
    }
  };

  return {
    fetchBankAccount,
    createBankAccount,
    updateBankAccount,
  };
};
